<template>
  <div class="tracking-page">
    <tracking-page-header
      title="GPS AI"
      subtitle="Claude-powered answers inside Tracking. Runs only when you click, and every answer is saved for the whole team."
      :show-dates="false"
    />

    <div class="trk-panel mb-3">
      <div class="trk-panel-head"><h2>Connection</h2></div>
      <div class="trk-panel-body">
        <p v-if="!status" class="text-muted mb-0">Checking…</p>
        <template v-else>
          <p class="gps-state" :class="status.connected ? 'ok' : 'off'">
            <i class="fa" :class="status.connected ? 'fa-check-circle' : 'fa-plug'"></i>
            {{ status.connected ? 'GPS AI is connected' : 'GPS AI is not connected' }}
            <span class="gps-model">model {{ status.model }}</span>
          </p>
          <p v-if="!status.connected" class="mb-2">{{ status.reason }}</p>
          <ul class="gps-checks">
            <li :class="status.keyConfigured ? 'ok' : 'off'">API key on the server: {{ status.keyConfigured ? 'set' : 'missing' }}</li>
            <li v-if="status.keyType === 'user'" :class="status.workspaceConfigured ? 'ok' : 'off'">
              Workspace ID (needed for this kind of key): {{ status.workspaceConfigured ? 'set' : 'missing' }}
            </li>
          </ul>
        </template>
      </div>
    </div>

    <div class="trk-panel mb-3">
      <div class="trk-panel-head"><h2>Where the keys go (security)</h2></div>
      <div class="trk-panel-body">
        <p>Keys are set <strong>only as environment variables on the backend server</strong>. They are never typed into this admin portal, never stored in the database, never written in the code repository, and never sent to any browser — this page only shows whether they are set.</p>
        <table class="table table-sm gps-env">
          <thead><tr><th>Environment variable</th><th>What it is</th><th>Where to get it</th></tr></thead>
          <tbody>
            <tr><td><code>ANTHROPIC_API_KEY</code></td><td>Required. The Anthropic API key GPS AI uses.</td><td>Anthropic Console → API Keys (create it inside the workspace you want billed)</td></tr>
            <tr><td><code>ANTHROPIC_WORKSPACE_ID</code></td><td>Only for keys not tied to a workspace (they start <code>sk-ant-usr-</code>).</td><td>Anthropic Console → Settings → Workspaces (starts <code>wrkspc_</code>)</td></tr>
          </tbody>
        </table>
        <p class="mb-1"><strong>Setting them</strong>: add both to the backend's environment on the dev and prod servers (the same place other server secrets are set), then restart the backend. This page turns green when it works.</p>
        <p class="mb-0 text-muted">Good practice: give GPS AI its own key, set a monthly spend limit for that workspace in the Anthropic Console, and rotate the key if it is ever shared in chat or email.</p>
      </div>
    </div>

    <div class="trk-panel mb-3">
      <div class="trk-panel-head"><h2>GPS AI features</h2></div>
      <div class="trk-panel-body">
        <div class="gps-features">
          <div v-for="f in features" :key="f.title" class="gps-feature">
            <span class="gps-badge">GPS AI</span>
            <h3>{{ f.title }}</h3>
            <p><strong>Problem it solves:</strong> {{ f.problem }}</p>
            <p><strong>Benefit:</strong> {{ f.benefit }}</p>
            <router-link :to="f.to">{{ f.where }} →</router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="trk-panel mb-3">
      <div class="trk-panel-head"><h2>Privacy and cost</h2></div>
      <div class="trk-panel-body">
        <ul class="mb-0">
          <li><strong>Nothing runs automatically.</strong> Claude is only called when an admin clicks a GPS AI button; there is no scheduler.</li>
          <li><strong>Personal data is removed before sending</strong>: names, emails, phone numbers, postcodes, call notes, health answers, request bodies and tokens are stripped on the server. Problems send only the shape of a request, never its values.</li>
          <li><strong>Saved once, seen by all.</strong> Each answer is stored with who ran it and when, so the team doesn't pay for the same question twice.</li>
          <li><strong>Always a fallback.</strong> The system calculations next to GPS AI keep working when GPS AI is off.</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';

export default {
  name: 'GpsAi',
  components: { TrackingPageHeader },
  data () {
    return {
      status: null,
      features: [
        {
          title: 'Why people aren\'t converting',
          problem: 'Dashboards show how many people didn\'t book, not why. Finding the cause means digging through drop-offs, errors and API failures by hand.',
          benefit: 'One plain-English cause with the numbers that prove it and the fix — for any date range, in seconds.',
          where: 'All journeys (top of the page)',
          to: '/tracking/sessions'
        },
        {
          title: 'Explain this journey',
          problem: 'A single journey can have hundreds of events; working out where and why a patient got stuck takes minutes per patient.',
          benefit: 'Support can see instantly what happened and whether to call the patient, before picking up the phone.',
          where: 'Open any journey in All journeys',
          to: '/tracking/sessions'
        },
        {
          title: 'Diagnose this problem',
          problem: 'Error groups show codes and endpoints that only developers understand, so fixes wait for someone technical to look.',
          benefit: 'A likely root cause, step-by-step fix for developers, and what the team should tell affected patients meanwhile.',
          where: 'Problems → click any problem',
          to: '/tracking/errors'
        }
      ]
    };
  },
  created () {
    this.$onboardingTrackingService.getGpsAiStatus()
      .then((res) => { this.status = (res && res.data) || null; })
      .catch(() => { this.status = { connected: false, reason: 'Could not reach the backend.' }; });
  }
};
</script>

<style scoped>
.gps-state {
  font-size: 1.1rem;
  font-weight: 700;
}
.gps-state.ok { color: #2f6f55; }
.gps-state.off { color: #a35b00; }
.gps-model {
  font-weight: 400;
  font-size: 0.85rem;
  color: #6b7a72;
  margin-left: 8px;
}
.gps-checks {
  margin: 0;
  padding-left: 18px;
}
.gps-checks .ok { color: #2f6f55; }
.gps-checks .off { color: #a35b00; }
.gps-env code {
  white-space: nowrap;
}
.gps-features {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}
@media (min-width: 992px) {
  .gps-features { grid-template-columns: repeat(3, 1fr); }
}
.gps-feature {
  background: #f1faf4;
  border: 1px solid #bfe3cc;
  border-radius: 10px;
  padding: 14px 16px;
}
.gps-feature h3 {
  font-size: 1rem;
  font-weight: 700;
  margin: 6px 0 8px;
}
.gps-feature p {
  font-size: 0.88rem;
  margin-bottom: 6px;
}
.gps-badge {
  display: inline-block;
  background: #3c8969;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  border-radius: 20px;
  padding: 2px 10px;
}
</style>
